/**
 * BridgeConstructorPanel — the demo's showpiece.
 *
 * A live, typed Bridge Constructor that lets the reader (a) choose
 * which adjunction-phase witnesses the bridge body carries, (b)
 * declare which grade the bridge claims, (c) pick a seam, and watch
 * the type-checker decide: admit, refuse by T1 (neutral grade),
 * refuse by T2 (grade-distinct), or refuse because the license field
 * is uninhabited (the claimed grade does not derive from the body).
 *
 * SPDX-License-Identifier: LicenseRef-Proprietary
 * Copyright (c) 2026 Invariant Holdings LLC
 */

import React, { useCallback, useMemo, useState } from "react";
import { SDPS } from "../data/sdps.js";
import { PanelSurface, panelTokens } from "@csk/demo-ui";
import {
  admit,
  deriveGrade,
  licenseInhabited,
  makeGradedBridge,
  type BridgeBody,
  type Seam,
} from "../algorithms/bridge.js";
import {
  componentKey,
  formatGrade,
  gradeEquals,
  isNeutral,
  neutralGrade,
  singletonGrade,
  type Grade,
  type Phase,
  type SdpCode,
} from "../algorithms/grade.js";
import { GradeVector } from "../components/GradeVector.js";

interface SeamOption {
  readonly id: string;
  readonly label: string;
  readonly accent: string;
  readonly seam: Seam;
  readonly summary: string;
}

const SEAM_OPTIONS: readonly SeamOption[] = SDPS.flatMap<SeamOption>((sdp) => {
  const eta: SeamOption = {
    id: `${sdp.code}-eta`,
    label: `σ_${sdp.code} expecting [${sdp.code}, η]`,
    accent: sdp.color,
    seam: {
      id: `${sdp.code}-eta`,
      label: `σ_${sdp.code}`,
      expectedGrade: singletonGrade(sdp.code, "eta"),
    },
    summary: `${sdp.name} unit seam — accepts the grant phase only.`,
  };
  const epsilon: SeamOption = {
    id: `${sdp.code}-epsilon`,
    label: `σ_${sdp.code} expecting [${sdp.code}, ε]`,
    accent: sdp.color,
    seam: {
      id: `${sdp.code}-epsilon`,
      label: `σ_${sdp.code}'`,
      expectedGrade: singletonGrade(sdp.code, "epsilon"),
    },
    summary: `${sdp.name} counit seam — accepts the discharge phase only.`,
  };
  return [eta, epsilon];
});

const PRESETS = [
  {
    id: "preset-honest",
    label: "Honest delegation grant",
    body: [{ sdp: "Dt" as SdpCode, phase: "eta" as Phase }],
    grade: singletonGrade("Dt", "eta"),
    seamId: "Dt-eta",
    note: "Body carries η_Dt; bridge claims [Dt, η]; seam expects [Dt, η]. Clean admit.",
  },
  {
    id: "preset-cd-1",
    label: "CD-1 — receipt presented as grant",
    body: [{ sdp: "Dt" as SdpCode, phase: "epsilon" as Phase }],
    grade: singletonGrade("Dt", "epsilon"),
    seamId: "Dt-eta",
    note: "Body carries ε_Dt; bridge claims [Dt, ε]; seam expects [Dt, η]. T2 refuses.",
  },
  {
    id: "preset-cd-4",
    label: "CD-4 — consumed token replay",
    body: [],
    grade: neutralGrade(),
    seamId: "St-epsilon",
    note: "Empty body lands at neutral grade; seam expects [St, ε]. T1 refuses.",
  },
  {
    id: "preset-forged",
    label: "Forged grade — body & claim disagree",
    body: [{ sdp: "Tr" as SdpCode, phase: "eta" as Phase }],
    grade: singletonGrade("Dt", "eta"),
    seamId: "Dt-eta",
    note: "Body carries η_Tr but the bridge claims [Dt, η]. License is uninhabited; not even a typable bridge.",
  },
] as const;

export function BridgeConstructorPanel(): React.JSX.Element {
  const [bodyWitnesses, setBodyWitnesses] = useState<
    readonly { sdp: SdpCode; phase: Phase }[]
  >([{ sdp: "Dt", phase: "eta" }]);
  const [claimedGrade, setClaimedGrade] = useState<Grade>(
    singletonGrade("Dt", "eta"),
  );
  const [seamId, setSeamId] = useState<string>("Dt-eta");
  const [autoSync, setAutoSync] = useState<boolean>(true);

  const seamOption = useMemo(() => {
    return (
      SEAM_OPTIONS.find((option) => option.id === seamId) ?? SEAM_OPTIONS[0]
    );
  }, [seamId]);

  const body: BridgeBody = useMemo(
    () => ({
      id: "interactive-body",
      witnesses: bodyWitnesses,
    }),
    [bodyWitnesses],
  );

  const derivedGrade = useMemo(() => deriveGrade(body), [body]);
  const effectiveGrade = autoSync ? derivedGrade : claimedGrade;
  const bridge = useMemo(
    () => makeGradedBridge(body, effectiveGrade),
    [body, effectiveGrade],
  );
  const verdict = useMemo(
    () => admit(bridge, seamOption.seam),
    [bridge, seamOption.seam],
  );

  const toggleWitness = useCallback((sdp: SdpCode, phase: Phase) => {
    setBodyWitnesses((current) => {
      const key = componentKey(sdp, phase);
      const exists = current.some(
        (entry) => componentKey(entry.sdp, entry.phase) === key,
      );
      if (exists) {
        return current.filter(
          (entry) => componentKey(entry.sdp, entry.phase) !== key,
        );
      }
      return [...current, { sdp, phase }];
    });
  }, []);

  const toggleClaimedComponent = useCallback((sdp: SdpCode, phase: Phase) => {
    setClaimedGrade((current) => {
      const next = { ...current } as Record<string, 0 | 1>;
      const key = componentKey(sdp, phase);
      next[key] = ((current[key] ?? 0) ^ 1) as 0 | 1;
      return Object.freeze(next);
    });
  }, []);

  const applyPreset = useCallback((presetId: string) => {
    const preset = PRESETS.find((p) => p.id === presetId);
    if (!preset) return;
    setBodyWitnesses(preset.body);
    setClaimedGrade(preset.grade);
    setSeamId(preset.seamId);
    setAutoSync(
      gradeEquals(
        preset.grade,
        deriveGrade({ id: "preset", witnesses: preset.body }),
      ),
    );
  }, []);

  const licenseStatus = licenseInhabited(body, effectiveGrade);

  return (
    <PanelSurface.PanelShell
      title="Bridge Constructor"
      subtitle="LIVE TYPED-ADMISSION PLAYGROUND · CONSTRUCT A BRIDGE · WATCH THE TYPE-CHECKER DECIDE"
      accentColor="#22d3ee"
    >
      <PanelSurface.Card glowColor="#22d3ee44">
        <PanelSurface.SectionHeading>How to use this panel</PanelSurface.SectionHeading>
        <p className="mt-2 mb-3 leading-[1.6]">
          Build a graded bridge from three independent pieces: pick which
          adjunction <strong>witnesses</strong> the bridge body carries, set the
          bridge's <strong>claimed grade</strong>, and choose the{" "}
          <strong>seam</strong> it tries to cross. The verdict at the bottom is
          exactly what the type-checker would emit for the{" "}
          <PanelSurface.InlineCode>admit</PanelSurface.InlineCode> function from chapter 4 §4.2.
        </p>
        <div className="flex flex-wrap gap-2">
          {PRESETS.map((preset) => (
            <button
              key={preset.id}
              type="button"
              onClick={() => {
                applyPreset(preset.id);
              }}
              className="cursor-pointer rounded-full border border-[var(--tcc-border)] bg-[rgba(5,8,16,0.55)] px-3 py-2 text-[12.5px] font-semibold text-[var(--tcc-text)]"
              title={preset.note}
            >
              {preset.label}
            </button>
          ))}
        </div>
      </PanelSurface.Card>

      <div className="grid grid-cols-[minmax(0,1fr)_minmax(0,1fr)] gap-[18px]">
        <PanelSurface.Card>
          <PanelSurface.SectionHeading>① Bridge body — witnesses</PanelSurface.SectionHeading>
          <p className="mt-2 mb-3 text-[13px] leading-[1.55] text-[var(--tcc-muted)]">
            Each lit cell adds a `(SDP, phase)` unit/counit datum to the body's
            payload. Click to toggle.
          </p>
          <GradeVector
            grade={derivedGrade}
            editable
            onToggle={toggleWitness}
            label="Body witnesses"
          />
          <div className="mt-3.5 flex flex-wrap items-center gap-2.5">
            <span className="font-mono text-[11.5px] text-[var(--tcc-muted)]">
              Derived grade:
            </span>
            <span className="font-mono text-[13px] font-bold text-[var(--tcc-bright)]">
              {formatGrade(derivedGrade)}
            </span>
          </div>
        </PanelSurface.Card>

        <PanelSurface.Card>
          <PanelSurface.SectionHeading>② Claimed grade</PanelSurface.SectionHeading>
          <p className="mt-2 mb-3 text-[13px] leading-[1.55] text-[var(--tcc-muted)]">
            The grade the bridge says it carries. When this disagrees with the
            body, the <PanelSurface.InlineCode>license</PanelSurface.InlineCode> field is uninhabited and
            the bridge is not even a typable{" "}
            <PanelSurface.InlineCode>GradedBridge</PanelSurface.InlineCode>.
          </p>
          <GradeVector
            grade={effectiveGrade}
            editable={!autoSync}
            onToggle={toggleClaimedComponent}
            label="Claimed grade"
          />
          <div className="mt-3.5">
            <label className="inline-flex cursor-pointer items-center gap-2 text-[12.5px]">
              <input
                type="checkbox"
                checked={autoSync}
                onChange={(event) => {
                  setAutoSync(event.target.checked);
                  if (event.target.checked) {
                    setClaimedGrade(derivedGrade);
                  }
                }}
              />
              Auto-derive claim from body (license trivially holds)
            </label>
          </div>
          <div className="mt-2.5">
            {licenseStatus
              ? PanelSurface.badge("license · inhabited", panelTokens.accentGreen)
              : PanelSurface.badge(
                  "license · uninhabited",
                  panelTokens.accentRed,
                  "Claimed grade disagrees with the body's witnesses; the bridge is not a typable GradedBridge.",
                )}
            {isNeutral(effectiveGrade)
              ? PanelSurface.badge("grade = 0_G", panelTokens.accentAmber)
              : null}
          </div>
        </PanelSurface.Card>
      </div>

      <PanelSurface.Card>
        <PanelSurface.SectionHeading>③ Choose a seam to cross</PanelSurface.SectionHeading>
        <p className="mt-2 mb-3 text-[13px] leading-[1.55] text-[var(--tcc-muted)]">
          Each seam declares an obligation —{" "}
          <PanelSurface.InlineCode>seamGrade(σ) : G</PanelSurface.InlineCode> — and admits a bridge only
          when its grade equals the obligation.
        </p>
        <PanelSurface.ChipRow
          items={SEAM_OPTIONS.map((option) => ({
            id: option.id,
            label: option.label,
            color: option.accent,
          }))}
          activeId={seamOption.id}
          onChange={setSeamId}
          ariaLabel="Seam choice"
        />
        <div className="mt-2.5 text-[12.5px] italic text-[var(--tcc-muted)]">
          {seamOption.summary}
        </div>
      </PanelSurface.Card>

      <PanelSurface.Card
        glowColor={verdict.outcome === "ADMITTED" ? "#22c55e44" : "#ef444444"}
      >
        <PanelSurface.SectionHeading>Verdict from admit(σ, b)</PanelSurface.SectionHeading>
        <div className="mt-2.5">
          <PanelSurface.VerdictBanner
            outcome={verdict.outcome}
            tag={
              verdict.outcome === "ADMITTED"
                ? `seam ${seamOption.seam.label}`
                : verdict.refusedBy === "license-uninhabited"
                  ? "license uninhabited"
                  : `refused by ${verdict.refusedBy}`
            }
            explanation={verdict.explanation}
          />
        </div>
        <div className="mt-4 flex flex-wrap gap-3">
          <PanelSurface.KpiBox
            label="bridge grade"
            value={formatGrade(effectiveGrade)}
            color={verdict.outcome === "ADMITTED" ? panelTokens.accentGreen : panelTokens.accentRed}
          />
          <PanelSurface.KpiBox
            label="seam obligation"
            value={formatGrade(seamOption.seam.expectedGrade)}
            color={seamOption.accent}
          />
          <PanelSurface.KpiBox
            label="license"
            value={licenseStatus ? "inhabited" : "uninhabited"}
            color={licenseStatus ? panelTokens.accentGreen : panelTokens.accentRed}
          />
        </div>
        <PanelSurface.Mono style={{ marginTop: 16 }}>
          {[
            "-- chapter 4 §4.2 — the typed admit function",
            "admit : (σ : Seam)",
            "      → (b : GradedBridge A B)",
            "      → grade b ≡ seamGrade σ",
            "      → AdmittedAt σ",
            "",
            `seamGrade σ = ${formatGrade(seamOption.seam.expectedGrade)}`,
            `grade b     = ${formatGrade(effectiveGrade)}`,
            `license     = ${licenseStatus ? "(inhabited — body and claim agree)" : "(uninhabited — body and claim disagree)"}`,
            "",
            verdict.outcome === "ADMITTED"
              ? "-- verdict: AdmittedAt σ inhabited"
              : `-- verdict: refused by ${verdict.refusedBy}`,
          ].join("\n")}
        </PanelSurface.Mono>
      </PanelSurface.Card>

      <PanelSurface.Card>
        <PanelSurface.SectionHeading>What this panel demonstrates</PanelSurface.SectionHeading>
        <ul className="mt-2.5 ml-[18px] p-0 text-[13.5px] leading-[1.7]">
          <li>
            <strong>T1 (ungraded-empty):</strong> when the bridge body has no
            witnesses, the derived grade is <PanelSurface.InlineCode>0_G</PanelSurface.InlineCode>; any
            seam expecting a real grade refuses by T1.
          </li>
          <li>
            <strong>T2 (grade-reflected):</strong> when the bridge grade does
            not equal the seam grade, the type-checker refuses; the two grades
            have disjoint inhabitants.
          </li>
          <li>
            <strong>license uninhabited:</strong> when the claimed grade
            disagrees with the body's witnesses, the bridge cannot even be
            formed as a <PanelSurface.InlineCode>GradedBridge</PanelSurface.InlineCode> — refusal happens
            earlier than seam admission.
          </li>
        </ul>
      </PanelSurface.Card>
    </PanelSurface.PanelShell>
  );
}
